import {
  ConversationItem,
  UpdateConversationItemResult
} from "@coxauto-ui/cxm/communications/interfaces";

interface LatestItemMaps {
  byKey: Map<number | string, ConversationItem>;
  byContent: Map<string, ConversationItem>;
}

interface UpdateResult {
  items: ConversationItem[];
  hasUpdates: boolean;
}

interface MatchingResult {
  matchedInProgressKeys: Set<number | string>;
}

// Utility functions
const createContentKey = (item: ConversationItem): string => {
  return `${item.subject?.trim()}-${item.content.text?.trim()}`;
};

const sortByDateOldestFirst = (
  items: ConversationItem[]
): ConversationItem[] => {
  return [...items].sort((a, b) => Date.parse(a.date) - Date.parse(b.date));
};

const isInProgressItem = (item: ConversationItem): boolean => {
  return (item.key as number) < 0;
};

// Item lookup services
const findCompatibleContentItemByKey = (
  targetItem: ConversationItem,
  latestItemMaps: LatestItemMaps
): ConversationItem | undefined => {
  const targetKey = createContentKey(targetItem);

  // First try exact match
  const exactMatch = latestItemMaps.byContent.get(targetKey);
  if (exactMatch) return exactMatch;

  // Then try to find any key that contains our target key
  for (const [mapKey, item] of latestItemMaps.byContent) {
    if (mapKey.startsWith(targetKey)) {
      return item;
    }
  }
  return undefined;
};

const createLatestItemMaps = (
  latestItems: ConversationItem[]
): LatestItemMaps => {
  const byKey = new Map<number | string, ConversationItem>();
  const byContent = new Map<string, ConversationItem>();

  latestItems.forEach((latestItem: ConversationItem) => {
    byKey.set(latestItem.key, latestItem);
    byContent.set(createContentKey(latestItem), latestItem);
  });

  return { byKey, byContent };
};

// Update logic
const determineItemUpdate = (
  currentItem: ConversationItem,
  latestItemByKey: ConversationItem | undefined,
  latestItemByContent: ConversationItem | undefined
): UpdateConversationItemResult => {
  const itemIsInProgress = isInProgressItem(currentItem);
  const hasStatusUpdate =
    latestItemByKey && currentItem.eventResult !== latestItemByKey.eventResult;

  // Priority 1: Key matching for processed items with status updates
  if (!itemIsInProgress && hasStatusUpdate) {
    return { updatedItem: latestItemByKey, wasUpdated: true };
  }

  // Priority 2: Content matching for in-progress items that became processed
  const itemBecameProcessed = itemIsInProgress && latestItemByContent;
  if (itemBecameProcessed) {
    return { updatedItem: latestItemByContent, wasUpdated: true };
  }

  return { updatedItem: currentItem, wasUpdated: false };
};

const updateExistingItems = (
  currentItems: ConversationItem[],
  latestItemMaps: LatestItemMaps
): UpdateResult => {
  let hasUpdates = false;

  const items = currentItems.map((currentItem: ConversationItem) => {
    const latestItemByKey = latestItemMaps.byKey.get(currentItem.key);
    const latestItemByContent = findCompatibleContentItemByKey(
      currentItem,
      latestItemMaps
    );

    const { updatedItem, wasUpdated } = determineItemUpdate(
      currentItem,
      latestItemByKey,
      latestItemByContent
    );

    if (wasUpdated) {
      hasUpdates = true;
    }

    return updatedItem;
  });

  return { items, hasUpdates };
};

const filterNewItems = (
  latestItems: ConversationItem[],
  currentItemKeys: Set<number | string>
): ConversationItem[] => {
  return latestItems.filter(item => !currentItemKeys.has(item.key));
};

// Matching services
const canMatchInProgressToOfficial = (
  inProgressItem: ConversationItem,
  officialItem: ConversationItem
): boolean => {
  return (
    officialItem.direction === "outbound" &&
    officialItem.recipientContactInfo === inProgressItem.recipientContactInfo &&
    (officialItem.subject?.trim() || "") ===
      (inProgressItem.subject?.trim() || "")
  );
};

const findInProgressMatches = (
  inProgressItems: ConversationItem[],
  outboundItems: ConversationItem[]
): MatchingResult => {
  const matchedInProgressKeys = new Set<number | string>();
  const matchedOfficialKeys = new Set<number | string>();

  const sortedInProgressItems = sortByDateOldestFirst(inProgressItems);
  const sortedOutboundItems = sortByDateOldestFirst(outboundItems);

  for (const officialItem of sortedOutboundItems) {
    if (matchedOfficialKeys.has(officialItem.key)) continue;

    for (const inProgressItem of sortedInProgressItems) {
      if (matchedInProgressKeys.has(inProgressItem.key)) continue;

      if (canMatchInProgressToOfficial(inProgressItem, officialItem)) {
        matchedInProgressKeys.add(inProgressItem.key);
        matchedOfficialKeys.add(officialItem.key);
        break;
      }
    }
  }

  return { matchedInProgressKeys };
};

const removeMatchedInProgressItems = (
  updatedCurrentItems: ConversationItem[],
  newItems: ConversationItem[]
): {
  finalCurrentItems: ConversationItem[];
  inProgressMatchingOccurred: boolean;
} => {
  const remainingInProgressItems = updatedCurrentItems.filter(isInProgressItem);
  const newOutboundItems = newItems.filter(
    item => item.direction === "outbound"
  );

  if (remainingInProgressItems.length === 0 || newOutboundItems.length === 0) {
    return {
      finalCurrentItems: updatedCurrentItems,
      inProgressMatchingOccurred: false
    };
  }

  const { matchedInProgressKeys } = findInProgressMatches(
    remainingInProgressItems,
    newOutboundItems
  );

  if (matchedInProgressKeys.size === 0) {
    return {
      finalCurrentItems: updatedCurrentItems,
      inProgressMatchingOccurred: false
    };
  }

  const finalCurrentItems = updatedCurrentItems.filter(
    item => !matchedInProgressKeys.has(item.key)
  );

  return { finalCurrentItems, inProgressMatchingOccurred: true };
};

// Merging and sorting
const combineAndSortItems = (
  newItems: ConversationItem[],
  finalCurrentItems: ConversationItem[]
): ConversationItem[] => {
  const mergedItems = [...newItems, ...finalCurrentItems];
  return sortByDateOldestFirst(mergedItems);
};

// Main orchestrator
export const mergeConversationItems = (
  latestItems: ConversationItem[],
  currentItems: ConversationItem[]
) => {
  const currentItemKeys = new Set(currentItems.map(item => item.key));
  const latestItemMaps = createLatestItemMaps(latestItems);
  const newItems = filterNewItems(latestItems, currentItemKeys);

  const { items: updatedCurrentItems, hasUpdates: itemsWereUpdated } =
    updateExistingItems(currentItems, latestItemMaps);

  const { finalCurrentItems, inProgressMatchingOccurred } =
    removeMatchedInProgressItems(updatedCurrentItems, newItems);

  const finalCurrentItemKeys = new Set(finalCurrentItems.map(item => item.key));
  const finalNewItems = filterNewItems(latestItems, finalCurrentItemKeys);

  const sortedMergedItems = combineAndSortItems(
    finalNewItems,
    finalCurrentItems
  );

  const hasChanges =
    newItems.length > 0 || itemsWereUpdated || inProgressMatchingOccurred;

  return {
    hasNewItems: hasChanges,
    items: sortedMergedItems
  };
};
